
import { defineComponent, ref, onMounted } from 'vue';
import PostGrid from './components/PostGrid.vue';
import PostFilter from "@/components/PostFilter.vue";
import SkeletonGrid from "@/components/SkeletonGrid.vue";
interface Post {
	id: number;
	title: {
		rendered: string;
	}
  acf: {
    title: string;
    title_color: string;
    title_position: string;
    hero_background_color: string;
    hero_background_type: string;
    company: string;
    '1_column_image': string;
    '2_column_image': string;
    '3_column_image': string;
    featured: boolean;
    exclude: boolean;
  };
  link: string;
  date: string;
}

type Category = {
  id: number,
  count: number,
  description?: string,
  link?: string,
  name: string,
  slug: string,
  parent?: number,
}

export default defineComponent({
	name: 'App',
	components: {
    SkeletonGrid,
		PostGrid,
    PostFilter
	},
	setup() {
		const posts = ref<Post[]>([]);
		const posts2 = ref<Post[]>([]);
		const posts3 = ref<Post[]>([]);
		const categories = ref<Category[]>([]);
		const currentCategory = ref<number|null>(null);
		const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;
    const loading = ref<boolean>(false);

    const baseUrl = location.hostname === 'localhost' ? apiBaseUrl : `${location.protocol}//${location.host}`;
    const WORK_API_URL = `${baseUrl}/wp-json/wp/v2/work?per_page=100&acf_format=standard`;
    const CATEGORIES_API_URL = `${baseUrl}/wp-json/wp/v2/categories`;

    const filterCategories = (categories: Category[]) => {
      const filteredCategories = categories.filter(el => {
        return el.slug !== 'uncategorized';
      });
      return filteredCategories;
    }

    const onCategoryChanged = (categoryId: number) => {
      loading.value = true;
      currentCategory.value = categoryId;

      const foundCategory = lookupCategoryById(categoryId);
      const queryString = new URLSearchParams(location.search);
      if (foundCategory) {
        queryString.set('category', foundCategory.slug);
      } else {
        queryString.delete('category');
      }

      const baseUrl = [location.protocol, '//', location.host, location.pathname].join('');
      const fullUrl = foundCategory ? `${baseUrl}?${queryString.toString()}` : baseUrl;
      history.replaceState({}, '', fullUrl);

      fetchData(categoryId?.toString());
    }

    const fetchData = async (categoryId?: string) => {
      const dataUrl = categoryId ? `${WORK_API_URL}&categories=${categoryId}` : WORK_API_URL;
      let response = await fetch(dataUrl);
      if (response.ok) {
        const data = await response.json();
        posts.value = [];
        posts2.value = [];
        posts3.value = [];

        const sortedData = sortAndFilterPosts(data);

        let length = data.length;
        const CONTAINER_1_MIN_THRESHOLD = 2;
        const CONTAINER_1_THRESHOLD = Math.ceil((length * 0.25));
        const CONTAINER_2_THRESHOLD = Math.ceil((length * 0.25));

        if (sortedData.length > CONTAINER_1_MIN_THRESHOLD) {
          posts.value = sortedData.slice(0, CONTAINER_1_THRESHOLD);

          if (sortedData.length < CONTAINER_1_THRESHOLD + CONTAINER_2_THRESHOLD) {
            posts2.value = sortedData.slice(CONTAINER_1_THRESHOLD);
          } else {
            posts2.value = sortedData.slice(CONTAINER_1_THRESHOLD, CONTAINER_1_THRESHOLD + CONTAINER_2_THRESHOLD);
            posts3.value = sortedData.slice(CONTAINER_1_THRESHOLD + CONTAINER_2_THRESHOLD);
          }
        } else {
          posts.value = data;
        }

        loading.value = false;
      } else {
        console.error('Failed to fetch Work posts');
        loading.value = false;
      }
    }

    const sortAndFilterPosts = (posts: Post[]) => {
      const sortedPosts = posts.sort((a: Post, b: Post) => {


        if (a.acf.featured === true && b.acf.featured === false) {
          return -1;
        } else {
          try {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            if (dateA > dateB) {
              return -1;
            } else {
              return 1;
            }
          } catch(e) {
            return 0;
          }
        }
      });

      const filteredPosts = sortedPosts.filter((el:Post) => {
        return !el.acf.exclude;
      });

      return filteredPosts;
    }

    const fetchCategories = async () => {
      const response = await fetch(CATEGORIES_API_URL);

      if (response.ok) {
        const data = await response.json();
        categories.value = filterCategories(data);

      } else {
        console.error('Failed to fetch Work categories');
      }
    }

    const lookupCategoryBySlug = (categorySlug: string): Category|null => {
      const foundCategory = categories.value.find(el => {
        return el.slug === categorySlug
      });

      return foundCategory ?? null;
    }

    const lookupCategoryById = (categoryId: number): Category|null => {
      const foundCategory = categories.value.find(el => {
        return el.id === categoryId
      });

      return foundCategory ?? null;
    }

		onMounted(async () => {
			try {
        loading.value = true;
        await fetchCategories();

        const queryString = new URLSearchParams(location.search);
        let foundCategory: Category|null = null;

        if (queryString && queryString.get('category')) {
          const categorySlug = queryString.get('category');
          foundCategory = lookupCategoryBySlug(categorySlug!);
        }

        if (foundCategory) {
          onCategoryChanged(foundCategory.id);
        } else {
          await fetchData();
        }
			} catch (error) {
				console.error(error);
			}
		});

		return {
			posts,
			posts2,
			posts3,
      categories,
      currentCategory,
      loading,
      onCategoryChanged
		};
	}
});
